import React from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { MessageCircle, Phone, ArrowRight } from "lucide-react";
import { whatsappLink, brandPhoneLink } from "../lib/whatsapp";
import Layout from "../components/Layout";
import HeroSplit from "../components/HeroSplit";
import AddressHero from "../components/AddressHero";
import ThreeStepFlow from "../components/ThreeStepFlow";
import BillingActivatedBanner from "../components/BillingActivatedBanner";
import ReferralBanner from "../components/ReferralBanner";
import GetStartedZipBanner from "../components/GetStartedZipBanner";
import GoogleReviewCards from "../components/GoogleReviewCards";
import BillingTransparency from "../components/BillingTransparency";
import StatCounters from "../components/StatCounters";
import Comparator from "../components/Comparator";
import RevealText from "../components/RevealText";

function Faded({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

export default function Home() {
  const { t } = useTranslation();

  return (
    <Layout>
      <Helmet>
        <title>Velcor Energy — {t("home.h1Pre")} {t("home.h1Accent")}</title>
        <meta name="description" content={t("home.subtitle")} />
      </Helmet>

      {/* HERO — Rural home photo + centered glass address card */}
      <HeroSplit>
        <div className="max-w-[1180px] mx-auto px-5 md:px-8 pt-8 md:pt-14 pb-14 md:pb-24" data-testid="hero">
          {/* Title block — fully centered on all viewports */}
          <div className="max-w-[860px] mx-auto text-center">
            <Faded>
              <div className="inline-flex items-center gap-2 mb-5 md:mb-6 mx-auto px-3 py-1 rounded-full bg-black/35 backdrop-blur-sm ring-1 ring-white/15">
                <span className="w-6 h-[2px] bg-velcor-green rounded-full" />
                <span
                  className="text-[11px] font-extrabold tracking-[0.24em] uppercase text-white"
                  style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}
                  data-testid="hero-kicker"
                >
                  {t("home.kicker", "Texas · 100% residencial")}
                </span>
              </div>

              <h1
                className="display text-[34px] sm:text-[42px] md:text-[50px] lg:text-[58px] xl:text-[64px] leading-[1.04] text-white"
                data-testid="hero-title"
              >
                <RevealText text={t("home.h1Pre")} />{" "}
                <RevealText
                  text={t("home.h1Accent")}
                  className="display-accent"
                  delay={0.18}
                />
                {t("home.h1Post")}
              </h1>
              <p className="mt-4 md:mt-5 text-white/80 text-[14.5px] md:text-[16.5px] leading-relaxed max-w-[560px] mx-auto">
                {t("home.heroSub")}
              </p>
            </Faded>
          </div>

          {/* Centered glass address search card */}
          <Faded delay={0.18}>
            <div className="mt-8 md:mt-12 mx-auto w-full max-w-[720px]" data-testid="hero-search-card">
              <div
                className="rounded-[32px] p-3 md:p-4 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] ring-1 ring-white/15"
                style={{
                  background: "rgba(255,255,255,0.94)",
                  backdropFilter: "blur(22px) saturate(120%)",
                  WebkitBackdropFilter: "blur(22px) saturate(120%)",
                }}
              >
                <AddressHero />
              </div>
            </div>
          </Faded>

          <Faded delay={0.28}>
            <div className="mt-6 text-center">
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-white/70 hover:text-velcor-green transition-colors group"
                data-testid="hero-how-it-works"
              >
                <span className="border-b border-white/30 group-hover:border-velcor-green transition-colors">
                  {t("home.heroLearnMore")}
                </span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </Faded>
        </div>
      </HeroSplit>

      {/* THREE-STEP RECAP */}
      <ThreeStepFlow />

      {/* STAT COUNTERS — animated big numbers */}
      <StatCounters />

      {/* COMPARATOR — Velcor vs others */}
      {/* Comparator removed per request */}

      {/* REFERRAL — image-right, copy-left ($25 referral program) */}
      <ReferralBanner />

      {/* BILLING / DEFERRED PAYMENT — image-left, copy-right banner */}
      <BillingActivatedBanner />

      {/* GET STARTED — Big purple-style banner with ZIP input */}
      <GetStartedZipBanner />

      {/* BILLING TRANSPARENCY — No contracts, free activation, Day 7-10 first payment */}
      <BillingTransparency />

      {/* TESTIMONIALS — Google-verified style */}
      <GoogleReviewCards />

      {/* Soft divider between Reviews and Help section — gradient line with green accent dot */}
      <div className="relative max-w-[1180px] mx-auto px-5 pt-2" aria-hidden>
        <div className="relative h-px">
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to right, transparent 0%, rgba(15,23,42,0.10) 25%, rgba(15,23,42,0.10) 75%, transparent 100%)",
            }}
          />
          <div className="absolute left-1/2 -translate-x-1/2 -top-1.5 w-3 h-3 rounded-full bg-velcor-green/90 shadow-[0_0_0_4px_rgba(47,122,58,0.12)]" />
        </div>
      </div>

      {/* HELP 24/7 — Mascot section (integrated, no card) */}
      <section
        className="relative overflow-hidden py-16 md:py-24"
        data-testid="help-24"
      >
        {/* Ambient background — Velcor green glow + grid */}
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(900px 500px at 18% 50%, rgba(47,122,58,0.10), transparent 65%), radial-gradient(700px 380px at 85% 65%, rgba(126,217,87,0.08), transparent 60%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 -z-10 opacity-[0.04] grid-bg pointer-events-none"
        />

        <div className="relative max-w-[1180px] mx-auto px-5">
          <Faded>
            <div className="relative grid md:grid-cols-[1.2fr_1fr] items-center gap-8 md:gap-14">
              {/* Copy */}
              <div className="text-center md:text-left">
                <h2
                  className="display text-velcor-ink leading-[1.05]"
                  style={{ fontSize: "clamp(30px, 4.4vw, 52px)" }}
                >
                  <RevealText text={t("home.helpTitle")} />{" "}
                  <RevealText
                    text={t("home.helpAccent")}
                    className="display-accent"
                    delay={0.16}
                  />
                </h2>

                <div className="mt-7 flex items-center justify-center md:justify-start gap-3">
                  <a
                    href={whatsappLink(t("whatsapp.prefilledMsg"))}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={t("home.helpWhatsapp")}
                    title={t("home.helpWhatsapp")}
                    className="
                      relative grid place-items-center rounded-full bg-velcor-green text-white
                      shadow-[0_10px_24px_rgba(47,122,58,0.30)]
                      hover:scale-[1.06] hover:shadow-[0_16px_32px_rgba(47,122,58,0.42)]
                      active:scale-95 transition-all duration-300 ease-out
                      help-pulse-ring
                    "
                    style={{ width: 52, height: 52 }}
                    data-testid="help-24-whatsapp"
                  >
                    <MessageCircle className="w-5 h-5" strokeWidth={2.2} />
                  </a>
                  <a
                    href={brandPhoneLink()}
                    aria-label={t("home.helpCall")}
                    title={t("home.helpCall")}
                    className="
                      grid place-items-center rounded-full bg-white border-[1.5px] border-velcor-ink/15 text-velcor-ink
                      hover:border-velcor-green hover:text-velcor-green
                      hover:scale-[1.06] active:scale-95 transition-all duration-300 ease-out
                    "
                    style={{ width: 52, height: 52 }}
                    data-testid="help-24-call"
                  >
                    <Phone className="w-5 h-5" strokeWidth={2.2} />
                  </a>
                </div>
              </div>

              {/* Mascot */}
              <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="relative flex items-center justify-center md:justify-end"
              >
                {/* Soft halo behind the mascot */}
                <div
                  aria-hidden
                  className="absolute inset-0 m-auto rounded-full blur-3xl opacity-60"
                  style={{
                    width: "70%",
                    height: "70%",
                    background:
                      "radial-gradient(circle, rgba(47,122,58,0.22), rgba(126,217,87,0.10) 50%, transparent 75%)",
                  }}
                />
                <img
                  src="/velcor-mascot.png"
                  alt="Velcor mascot"
                  className="relative w-[200px] sm:w-[240px] md:w-[280px] lg:w-[320px] drop-shadow-[0_30px_50px_rgba(15,23,42,0.20)]"
                  data-testid="help-24-mascot"
                />
              </motion.div>
            </div>
          </Faded>
        </div>
      </section>
    </Layout>
  );
}
